/**
 * 本机音频设备测试台：
 * - 枚举麦克风/扬声器；
 * - 麦克风实时电平（WebAudio Analyser）；
 * - 扬声器测试音（可路由到指定输出设备，若浏览器支持 setSinkId）。
 *
 * Phase 1 只做本机检测；训练中的实际选麦/选输出后续接入 MediaConstraints。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { updateSettings } from '../../state/settings.ts';

interface DeviceOption {
  deviceId: string;
  label: string;
}

export function AudioDeviceLab({ onClose }: { onClose: () => void }) {
  const [inputs, setInputs] = useState<DeviceOption[]>([]);
  const [outputs, setOutputs] = useState<DeviceOption[]>([]);
  const [inputId, setInputId] = useState('');
  const [outputId, setOutputId] = useState('');
  const [level, setLevel] = useState(0);
  const [testingMic, setTestingMic] = useState(false);
  const [testingSpeaker, setTestingSpeaker] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  const enumerate = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setError('当前浏览器不支持 enumerateDevices');
      return;
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const microphone = devices
      .filter((device) => device.kind === 'audioinput')
      .map((device, index) => ({ deviceId: device.deviceId, label: device.label || `麦克风 ${index + 1}` }));
    const speaker = devices
      .filter((device) => device.kind === 'audiooutput')
      .map((device, index) => ({ deviceId: device.deviceId, label: device.label || `扬声器 ${index + 1}` }));
    setInputs(microphone);
    setOutputs(speaker);
    const savedInput = (() => { try { return localStorage.getItem('zongheng.audioInputId') ?? ''; } catch { return ''; } })();
    const savedOutput = (() => { try { return localStorage.getItem('zongheng.audioOutputId') ?? ''; } catch { return ''; } })();
    setInputId((current) => current || savedInput || microphone[0]?.deviceId || '');
    setOutputId((current) => current || savedOutput || speaker[0]?.deviceId || '');
  }, []);

  useEffect(() => {
    void enumerate();
    const onChange = () => void enumerate();
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);
    return () => {
      navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
      stopEverything();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enumerate]);

  const stopEverything = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void audioContextRef.current?.close();
    audioContextRef.current = null;
    setTestingMic(false);
    setLevel(0);
  };

  const testMic = async () => {
    setError(null);
    stopEverything();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: inputId ? { deviceId: { exact: inputId } } : true,
      });
      streamRef.current = stream;
      const context = new AudioContext();
      audioContextRef.current = context;
      await context.resume();
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const data = new Uint8Array(analyser.fftSize);
      setTestingMic(true);
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (const value of data) {
          const normalized = Math.abs(value - 128) / 128;
          if (normalized > peak) peak = normalized;
        }
        setLevel(Math.min(1, peak * 2.2));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
      // 获取到权限后 label 才有值，刷新一次设备名
      await enumerate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      stopEverything();
    }
  };

  const testSpeaker = async () => {
    setError(null);
    setTestingSpeaker(true);
    try {
      const context = new AudioContext();
      await context.resume();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 660;
      gain.gain.value = 0.06;
      oscillator.connect(gain);
      const destination = context.createMediaStreamDestination();
      gain.connect(destination);
      if (outputId) {
        const audio = new Audio();
        audio.srcObject = destination.stream;
        const sink = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
        if (sink.setSinkId) await sink.setSinkId(outputId);
        await audio.play().catch(() => undefined);
        oscillator.start();
        oscillator.stop(context.currentTime + 1.2);
        setTimeout(() => {
          audio.pause();
          audio.srcObject = null;
          void context.close();
          setTestingSpeaker(false);
        }, 1300);
      } else {
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + 1.2);
        setTimeout(() => {
          void context.close();
          setTestingSpeaker(false);
        }, 1300);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setTestingSpeaker(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" data-testid="device-lab">
      <section className="lens w-full max-w-[520px] p-4">
        <header className="flex items-start justify-between gap-3">
          <div>
            <div className="micro-label">AUDIO DEVICE LAB</div>
            <h2 className="mt-0.5 text-[15px] font-semibold text-slate-100">麦克风 / 扬声器自检</h2>
            <p className="mt-1 text-[10.5px] text-slate-500">
              先授权麦克风；这里只做本机测试，不改变房间里的实际轨道。
            </p>
          </div>
          <button type="button" className="transport-btn transport-btn--icon" onClick={onClose} aria-label="关闭设备测试">×</button>
        </header>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="micro-label">麦克风输入</span>
            <select
              data-testid="device-input-select"
              value={inputId}
              onChange={(event) => {
                const value = event.target.value;
                setInputId(value);
                try { localStorage.setItem('zongheng.audioInputId', value); } catch { /* ignore */ }
                updateSettings({ audioInputId: value });
              }}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-2 py-2 text-[12px] text-slate-200"
            >
              {inputs.length === 0 && <option value="">未发现麦克风</option>}
              {inputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>{device.label}</option>
              ))}
            </select>
          </label>
          <div className="rounded-lg bg-white/[0.025] p-2.5">
            <div className="flex items-center justify-between">
              <span className="micro-label">输入电平</span>
              <button type="button" className="transport-btn !min-h-7 !py-1" onClick={() => void testMic()} data-testid="device-mic-test">
                {testingMic ? '重新测试' : '测试麦克风'}
              </button>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-black/50">
              <div
                data-testid="mic-level"
                className="h-full rounded-full bg-gradient-to-r from-[#7DD3FC] to-[#E2E8F0] transition-[width] duration-75"
                style={{ width: `${Math.round(level * 100)}%` }}
              />
            </div>
            <div className="mt-1 text-[9px] text-slate-500">{testingMic ? '说话看电平；无输入时保持 0' : '尚未开始测试'}</div>
          </div>

          <label className="block">
            <span className="micro-label">扬声器输出</span>
            <select
              data-testid="device-output-select"
              value={outputId}
              onChange={(event) => {
                const value = event.target.value;
                setOutputId(value);
                try { localStorage.setItem('zongheng.audioOutputId', value); } catch { /* ignore */ }
                updateSettings({ audioOutputId: value });
                window.dispatchEvent(new CustomEvent('zongheng:audio-output', { detail: value }));
              }}
              className="mt-1 w-full rounded-md border border-white/10 bg-black/30 px-2 py-2 text-[12px] text-slate-200"
            >
              {outputs.length === 0 && <option value="">默认输出</option>}
              {outputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>{device.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="transport-btn w-full"
            onClick={() => void testSpeaker()}
            data-testid="device-speaker-test"
            disabled={testingSpeaker}
          >
            {testingSpeaker ? '正在播放测试音…' : '播放 1.2 秒测试音'}
          </button>

          {inputs.length === 0 && (
            <p className="text-[10px] text-[#FDE68A]">未枚举到设备：请检查浏览器权限，或当前环境没有音频设备。</p>
          )}
          {error && <p className="text-[10px] text-[#FECDD3]" role="alert">{error}</p>}
        </div>
      </section>
    </div>
  );
}
